import React, { createRef, Fragment, useEffect, useState } from 'react';

import PropTypes from 'prop-types';
import { Divider, ExpandCollapse, Typography } from '@telus-uds/components-web';
import _ from 'lodash';

import request from '../utils/request';
import { useLocalizedStringsContext } from '../context/LocalizedStringsContext';
import {
  getMultiCheckboxOptions,
  normalizedFields,
  findField,
  getFieldName,
  getFieldValue,
  getFieldType,
  getFieldChecked
} from './utils/formCommon';
import { ProgressBar } from '../design';
import StatusBar from '../businessApps/statusBar';
import { Box } from '../design/css';
import {
  GroupTitle,
  GroupWrapper,
  GroupWrapperStyled,
  SectionHeaderTitle,
  SummaryGroupWrapper
} from './css';
import {
  quoteStatus,
  businessAppsPrequalificationForm,
  businessAppsOrderForm,
  fieldProductInOrderFor,
  fieldAccountNumberBANBA,
  fieldCustomerInvoiceForBA,
  fieldExistingBusinessConnectOrWirelessAccountBA,
  keyBusinessAppPlans,
  fieldSameAsAdminContact,
  fieldPinBA,
  fieldAddProductBA,
  fieldOrderRequestTypeBA,
  fieldExistingCustomerBA,
  fieldExistingCustomerDomain,
  fieldAttachmentsBA,
  fieldStreetNumberAddress,
  fieldMsaEContractBA,
  fieldBusinessIncorporationNumberBA,
  fieldBusinessIncorporativeDateBA,
  fieldSubmittingOnBehalfOf,
  fieldBusinessAppBillingNoteBA,
  fieldDiscountedPricing
} from '../utils/constant';
import {
  contentfulFormConfig,
  getBusinessAppFormStepFields
} from '../contentful';
import OrderDetails from '../businessApps/orderDetails';

import UserDetailsForm from './userDetailsForm';
import Note from '../note';
import FormSummaryGroup from './formSummaryGroup';
import RepeatedForm from './repeatedForm';
import stringInterpolation from '../utils/stringInterpolation';
import FormField from './formField';

const formFieldsGroup = 'formFieldsGroup';
const formSection = 'formSection';
const userDetailsKey = 'userDetails';
const repeatedFormKey = 'repeatedForm';
const requiredValue = 'required';
const profile = request.getProfile();

const FormStepBusinessApp = ({
  stepData,
  steps,
  step,
  setEligible,
  onFormSubmit,
  onSaveAndExit,
  onSave,
  onViewDocx,
  orderData,
  prevStep,
  onCancel,
  hideProgressBar,
  setOrderData,
  formName,
  createNewBusinessAppOrder
}) => {
  if (!stepData) return null;
  const localizedStrings = useLocalizedStringsContext();
  const {
    contentfulValues: { yes, no },
    modalForm: { fieldRequiredMessage, cannotProceedMessage },
    attachmentsBA: { requiredErrorMessage },
    formStep: {
      minValueErrorMessage,
      maxValueErrorMessage,
      minLengthErrorMessage,
      maxLengthErrorMessage,
      pinNotStartWithZero,
      pinNotBeRepetitive,
      pinNotBeSequential
    },
    formButtons: { newOrderButtonLabel, submittingButtonLabel },
    customerInvoiceForBA: { newBan },
    productInOrderForOptions: { googleWorkspace, microsoft365, esChat },
    existingCustomerBA_QuestionLabels: {
      googleWorkspaceCustomer,
      googleWorkspaceDomain,
      microsoft365Customer,
      microsoft365Domain
    }
  } = localizedStrings;
  const {
    fields: { name: formStepName, formStepFields, formSummary, modalForms }
  } = stepData;

  const [isLoading, setIsLoading] = useState(true);
  const [isFormTouched, setIsFormTouched] = useState(false);
  const [isFormValidated, setIsFormValidated] = useState(false);
  const [formFields, setFormFields] = useState({});
  const [formErrors, setFormErrors] = useState({});
  const [sections, setSections] = useState([]);
  const [dueNow, setDueNow] = useState(0);
  const [dueMonthly, setDueMonthly] = useState(0);
  const [isFieldReset, setIsFieldReset] = useState(false);
  const [prequalEligibility, setPrequalEligibility] = useState(false);
  const formStepRef = createRef();

  const [isOderDetailsOpen, setIsOrderDetailsOpen] = useState(false);
  const [orderSummary, setOrderSummary] = useState(null);
  const [orderComments, setOrderComments] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const userRights = {
    isStatusEditable: false,
    canAssignOrder: false,
    canAddComments: false,
    canPerformAction: false
  };

  const isSalesManager = profile.role === 7;
  const isSalesSupport = profile.role === 11;
  const isAdmin = profile.role === 1;
  const isSalesRep = profile.role === 2;

  const formSections = formStepFields.filter(({ sys }) => {
    const {
      contentType: {
        sys: { id }
      }
    } = sys;

    return id === formSection;
  });

  const [openPanels, setOpenPanels] = useState(() => {
    // Calculate initial open panels on first render
    return sections.reduce((acc, section) => {
      const {
        fields: { name, isPanelInitiallyOpen, formFields: sectionFields }
      } = section;

      if (isPanelInitiallyOpen) {
        acc.push(name);
      } else {
        const isErrors =
          sectionFields &&
          sectionFields.find(item => {
            const {
              fields: { name: itemName }
            } = item;
            return formErrors[itemName];
          });
        if (typeof isErrors !== 'undefined') acc.push(name);
      }

      return acc;
    }, []);
  });

  useEffect(() => {
    // Recalculate when formErrors change (for validation errors)
    const open = sections.reduce((acc, section) => {
      const {
        fields: { name, isPanelInitiallyOpen, formFields: sectionFields }
      } = section;

      if (isPanelInitiallyOpen) {
        acc.push(name);
      } else {
        const isErrors =
          sectionFields &&
          sectionFields.find(item => {
            const {
              fields: { name: itemName }
            } = item;
            return formErrors[itemName];
          });
        if (typeof isErrors !== 'undefined') acc.push(name);
      }

      return acc;
    }, []);
    
    setOpenPanels(open);
  }, [formErrors]);

  const isRelatedFieldExisted = (field, fields) => {
    const {
      appearsAfterField,
      appearsAfterValue,
      backendFieldValidation
    } = field;

    if (!appearsAfterField && !backendFieldValidation) return true;
    let result = true;
    let i = 0;
    if (appearsAfterField) {
      while (i < appearsAfterField.length && result) {
        const relatedFieldName = getFieldName(
          appearsAfterField[i],
          orderData,
          formName,
          yes
        );
        const relatedFieldType = getFieldType(relatedFieldName, fields);
        const relatedFieldValue =
          relatedFieldType === 'Checkbox'
            ? getFieldChecked(relatedFieldName, fields)
            : getFieldValue(relatedFieldName, fields);
        if (relatedFieldName) {
          if (
            fields[relatedFieldName] &&
            fields[relatedFieldName].isVisible &&
            relatedFieldValue
          ) {
            if (appearsAfterValue) {
              let isValueSelected;
              if (_.isArray(relatedFieldValue)) {
                isValueSelected = appearsAfterValue.find(item =>
                  relatedFieldValue.includes(item.fields.value)
                );
              } else {
                isValueSelected = appearsAfterValue.find(
                  item => relatedFieldValue === item.fields.value
                );
              }
              result = result && typeof isValueSelected !== 'undefined';
            }
          } else if (orderData && orderData[relatedFieldName]) {
            if (fields[relatedFieldName]) {
              if (
                (isFormTouched &&
                  orderData[relatedFieldName] === relatedFieldValue) ||
                !isFormTouched
              ) {
                if (appearsAfterValue) {
                  const isValueSelected = appearsAfterValue.find(
                    item => orderData[relatedFieldName] === item.fields.value
                  );
                  result = result && typeof isValueSelected !== 'undefined';
                }
              } else result = false;
            } else if (appearsAfterValue) {
              const isValueSelected = appearsAfterValue.find(
                item => orderData[relatedFieldName] === item.fields.value
              );
              result = result && typeof isValueSelected !== 'undefined';
            }
          } else {
            result = false;
          }
        }
        i += 1;
      }
    }

    if (backendFieldValidation) {
      let isVisible = result;
      for (let index = 0; index < backendFieldValidation.length; index += 1) {
        const {
          fields: { key, value }
        } = backendFieldValidation[index];
        isVisible =
          isVisible &&
          (value.split('|').includes(orderData[key]) ||
            (value.split('|').includes('undefined') &&
              typeof orderData[key] === 'undefined'));
      }
      return isVisible;
    }

    return result;
  };

  const getHiddenSections = fields => {
    const hiddenSections = [];

    formSections.forEach(section => {
      const {
        fields: { name: sectionName }
      } = section;
      if (!isRelatedFieldExisted(section.fields, fields)) {
        hiddenSections.push(sectionName);
      }
    });

    return hiddenSections;
  };

  const updateSections = updatedSections => {
    let newSections = [...formSections];
    updatedSections.forEach(item => {
      newSections = newSections.filter(section => section.fields.name !== item);
    });
    setSections(newSections);
  };

  const checkPrequalEligibility = () => {
    const valueSubmittingOnBehalfOf = getFieldValue(
      fieldSubmittingOnBehalfOf,
      formFields
    );
    const valueProductInOrderFor = getFieldValue(
      fieldProductInOrderFor,
      formFields
    );
    setPrequalEligibility(
      Boolean(valueSubmittingOnBehalfOf && valueProductInOrderFor)
    );
  };

  useEffect(() => {
    if (formName === businessAppsPrequalificationForm)
      checkPrequalEligibility();
  }, [formFields]);

  useEffect(() => {
    if (formStepFields && !isFieldReset) {
      const updatedFields = normalizedFields(
        formStepFields,
        formFields,
        orderData
      );
      setFormFields(updatedFields);
      updateSections(getHiddenSections(updatedFields));
      setIsLoading(false);
      setIsFormTouched(false);
      if (formStepRef && formStepRef.current)
        formStepRef.current.scrollTo(0, 0);
    }
  }, [formStepFields, orderData]);

  useEffect(() => {
    if (orderData.businessAppPlans && orderData.businessAppPlans.length > 0) {
      formFields[keyBusinessAppPlans] = orderData.businessAppPlans;
    }
    if (
      formFields[fieldAttachmentsBA] &&
      orderData.attachmentsBA &&
      orderData.attachmentsBA.length > 0
    ) {
      formFields[fieldAttachmentsBA].value = orderData.attachmentsBA;
    }
  }, []);

  useEffect(() => {
    if (step === steps.length) {
      if (orderData.businessAppPlans && orderData.businessAppPlans.length > 0) {
        orderData.businessAppPlans.map(item => {
          let monthlyCount = 0;
          let dueNowCount = 0;
          orderData.businessAppPlans.map(item => {
            monthlyCount =
              monthlyCount + (item.monthlyCharges * item.quantity || 0);
            dueNowCount =
              dueNowCount + (item.oneTimeCharges * item.quantity || 0);
          });
          setDueNow(dueNowCount);
          setDueMonthly(monthlyCount);
        });
      }
    }
  }, [step]);

  const sectionIsVisible = name =>
    typeof sections.find(item => item.fields.name === name) !== 'undefined';

  const getRepeatedFormErrors = (contentfulFields, formValues) => {
    const fields = contentfulFields.filter(
      ({ fields: content }) => content.required
    );
    const filteredData = formValues.filter(formValue => {
      const filteredValues = fields.filter(
        ({ fields: { name: formKeyValue } }) => !formValue[formKeyValue]
      );
      return filteredValues.length > 0;
    });
    return filteredData;
  };

  const msgMinValue = value => stringInterpolation(minValueErrorMessage, value);
  const msgMaxValue = value => stringInterpolation(maxValueErrorMessage, value);
  const msgMinLength = value =>
    stringInterpolation(minLengthErrorMessage, value);
  const msgMaxLength = value =>
    stringInterpolation(maxLengthErrorMessage, value);

  const getErrors = (data, errors) => {
    let result = errors;

    if (!data) return result;
    data.forEach(({ sys, fields }) => {
      const {
        contentType: {
          sys: { id }
        }
      } = sys;
      const { ineligible } = fields;
      const key = fields.name;
      const {
        value,
        minValue,
        maxValue,
        isRequired,
        isVisible,
        ref,
        minLength,
        maxLength,
        type,
        checked
      } =
        formFields &&
        Object.prototype.hasOwnProperty.call(formFields, key) &&
        formFields[key];

      /** Delete key if it exists because of an earlier error */
      if (key in result) delete result[key];

      if (id === formFieldsGroup && isRelatedFieldExisted(fields, formFields)) {
        result = getErrors(fields.fields, result);
      } else if (id === formSection && sectionIsVisible(key)) {
        result = getErrors(fields.formFields, result);
      } else if (id === userDetailsKey) {
        result = getErrors(fields.formStepFields, result);
      } else if (id === repeatedFormKey) {
        const repeatedFormErrors = getRepeatedFormErrors(
          fields.formFields,
          formFields[fields.key]
        );
        if (repeatedFormErrors.length) {
          result[fields.key] = repeatedFormErrors;
        } else {
          delete result[fields.key];
        }
      } else if (isVisible) {
        if (isRequired && type !== 'Checkbox') {
          if (!value && isRelatedFieldExisted(fields, formFields)) {
            let errorMessage = fieldRequiredMessage;
            if (fields.name === fieldAttachmentsBA) {
              errorMessage = requiredErrorMessage;
            }
            result[key] = { text: errorMessage, ref };
          } else {
            delete result[key];
          }
        }
        if (ineligible) {
          const {
            fields: { value: ineligibleValue }
          } = ineligible;
          const isIneligibleValue = ineligibleValue.split('|').includes(value);
          if (isIneligibleValue) {
            result[key] = { text: cannotProceedMessage, ref };
          }
        }
        if (isRequired && type === 'Checkbox') {
          if (!checked && isRelatedFieldExisted(fields, formFields))
            result[key] = { text: fieldRequiredMessage, ref };
          else delete result[key];
        }

        if (typeof minValue !== 'undefined' && parseFloat(value) < minValue) {
          result[key] = { text: msgMinValue(minValue), ref };
        }
        if (typeof maxValue !== 'undefined' && parseFloat(value) > maxValue) {
          result[key] = { text: msgMaxValue(maxValue), ref };
        }
        if (minLength && value && value.length < minLength) {
          result[key] = { text: msgMinLength(minLength), ref };
        }
        if (maxLength && value && value.length > maxLength) {
          result[key] = { text: msgMaxLength(maxLength), ref };
        }

        if (fields.fieldValidation && fields.fieldValidation.fields) {
          if (
            typeof fields.appearsAfterField === 'undefined' ||
            (typeof fields.appearsAfterField !== 'undefined' &&
              isRelatedFieldExisted(fields, formFields))
          ) {
            const {
              errorMessage,
              logic,
              value: expectedValue
            } = fields.fieldValidation.fields;
            let hasError = false;
            switch (logic) {
              case 'equal':
                // eslint-disable-next-line eqeqeq
                hasError = !expectedValue.split('|').includes(value);
                break;
              case 'regex':
                {
                  const regexLogic = new RegExp(expectedValue);
                  hasError = !regexLogic.test(value);
                }
                break;
              case 'gt':
                hasError = parseInt(value, 10) < parseInt(expectedValue, 10);
                break;
              case 'gte':
                hasError = parseInt(value, 10) <= parseInt(expectedValue, 10);
                break;
              case 'lt':
                hasError = +value < +expectedValue;
                break;
              default:
            }
            if (hasError) result[key] = { text: errorMessage, ref };
          }
        }
      }
    });

    /**
     * Condition for Pin number field
     * 1) Must contain between 4 and 6 digits
     * 2) Cannot start with zero
     * 3) Cannot be repetitive (ex. 0000, 1111, 9999)
     * 4) Cannot be sequential (ex. 1234, 5678)
     */
    if (formFields[fieldPinBA] && formFields[fieldPinBA].isVisible) {
      if (formFields[fieldPinBA]) {
        const valuePin = getFieldValue(fieldPinBA, formFields);
        if (!valuePin)
          result[fieldPinBA] = {
            text: fieldRequiredMessage,
            ref: formFields[fieldPinBA].ref
          };
        else if (valuePin.indexOf('0') === 0)
          result[fieldPinBA] = {
            text: pinNotStartWithZero,
            ref: formFields[fieldPinBA].ref
          };
        else {
          // not be repetitive (ex. 0000, 1111, 9999)
          const isRepetitive = valuePin
            .split('')
            .filter(item => item !== valuePin[0]);
          if (!isRepetitive.length) {
            result[fieldPinBA] = {
              text: pinNotBeRepetitive,
              ref: formFields[fieldPinBA].ref
            };
          } else {
            /**
             *
             * Can pass upto 2 sequential ie. (12, 34, 45)
             * Cannot pass more then 2 sequence ie. (123, 4567, 12345, 345678)
             */
            const pinDigits = valuePin.split('').map(item => Number(item));
            let isSequential = true;
            let i = 1;
            while (i < pinDigits.length && isSequential) {
              if (pinDigits[i] - pinDigits[i - 1] !== 1) isSequential = false;
              i += 1;
            }
            if (isSequential) {
              result[fieldPinBA] = {
                text: pinNotBeSequential,
                ref: formFields[fieldPinBA].ref
              };
            }
          }
        }
      }
    }
    return result;
  };

  const getFormData = () => {
    const result = Object.keys(formFields)
      .filter(key => typeof formFields[key].value !== 'undefined')
      .reduce((acc, key) => {
        const { type, value: oldValue, checked, isVisible } = formFields[key];
        const value = isVisible ? oldValue : '';
        acc[key] = type === 'Checkbox' ? checked : value;
        return acc;
      }, {});
    if (formFields[keyBusinessAppPlans]) {
      const field = findField(formStepFields, fieldAddProductBA);
      if (field) {
        if (isRelatedFieldExisted(field.fields, formFields)) {
          result[keyBusinessAppPlans] = formFields[keyBusinessAppPlans];
        } else {
          result[keyBusinessAppPlans] = [];
        }
      } else {
        result[keyBusinessAppPlans] = formFields[keyBusinessAppPlans];
      }
    }
    return result;
  };

  const validateForm = e => {
    e.preventDefault();
    const errors = getErrors(formStepFields, { ...formErrors });
    setFormErrors(errors);
    if (Object.entries(errors).length === 0) {
      const fields = getFormData();
      const newStatus = quoteStatus.approved;

      onSave({ ...fields, quoteStatus: newStatus }).then(() => {
        setIsFormValidated(true);
      });
    } else {
      const firstError = errors[Object.keys(errors)[0]];
      const { ref } = firstError;
      if (ref && ref.current) ref.current.focus();
    }
  };

  const handleViewOrder = async () => {
    const { id, locale } = orderData;
    setIsLoading(true);
    const {
      data: { results: comments }
    } = await request.get(`/orders/${id}/comments`);
    setOrderComments(comments);

    const config = { ...contentfulFormConfig, locale };
    getBusinessAppFormStepFields(config, summary => {
      setOrderSummary(summary);
      setIsLoading(false);
      setIsOrderDetailsOpen(true);
    });
  };

  const saveAndExit = () => {
    const fields = getFormData();
    if (Object.entries(fields).length) {
      onSaveAndExit({ ...fields });
    }
  };

  const onViewQuoteAsDocx = () => {
    const errors = getErrors(formStepFields, { ...formErrors });
    setFormErrors(errors);
    if (Object.entries(errors).length === 0) {
      const fields = getFormData();
      onViewDocx({ ...fields });
    } else {
      const firstError = errors[Object.keys(errors)[0]];
      const { ref } = firstError;
      if (ref && ref.current) ref.current.focus();
    }
  };

  const nextStep = e => {
    if (isSubmitting) return;
    e.preventDefault();
    const errors = getErrors(formStepFields, { ...formErrors });
    setFormErrors(errors);
    if (Object.entries(errors).length === 0) {
      setIsSubmitting(true);
      try {
        const fields = getFormData();
        onFormSubmit({ ...orderData, ...fields });
      } finally {
        setTimeout(() => setIsSubmitting(false), 2000);
      }
    } else {
      const firstError = errors[Object.keys(errors)[0]];
      const { ref } = firstError;
      if (ref && ref.current) ref.current.focus();
    }
  };

  const resetFormFields = (fields, currentFieldName) => {
    const { resetFields, customResetFields } = fields[currentFieldName];
    let isResetRequired = false;
    const newFields = { ...fields };
    for (let index = 0; index < formStepFields.length; index += 1) {
      const {
        fields: { name, appearsAfterField, value }
      } = formStepFields[index];

      if (name === currentFieldName) {
        isResetRequired = true;
      } else if (isResetRequired) {
        const field = fields[name];
        if (appearsAfterField && field.isVisible)
          newFields[name] = { ...field, isVisible: false, value };
      }
    }
    if (resetFields) {
      resetFields.forEach(field => {
        const fieldName = _.get(field, 'fields.name', '');
        if (fieldName && _.has(newFields[fieldName], 'value'))
          newFields[fieldName].value = '';
      });
    }
    if (customResetFields) {
      customResetFields.forEach(field => {
        const fieldName = _.get(field, 'fields.key', '');
        if (fieldName && _.has(newFields[fieldName], 'value'))
          newFields[fieldName].value = '';
        else delete newFields[fieldName];
      });
    }
    return newFields;
  };

  const isFieldCalculated = (type, value, checked, price) => {
    switch (type) {
      case 'Radio':
        return value === yes && price;
      case 'Checkbox':
        return checked && price;
      default:
        return value && price;
    }
  };

  const updateTotals = (name, field) => {
    const { value, type, price, monthlyPayment, isVisible } = field;
    if (!isVisible) return;

    let oldQuantity;
    let newQuantity;
    let oldPrice;
    let newPrice;

    if (type === 'Number') {
      oldQuantity = (formFields[name] && formFields[name].value) || 0;
      newQuantity = value ? Number(value) : 0;
      if (price > 0) {
        oldPrice = price * oldQuantity;
        newPrice = price * newQuantity;
        setDueNow(dueNow - oldPrice + newPrice);
      }
      if (monthlyPayment > 0) {
        oldPrice = monthlyPayment * oldQuantity;
        newPrice = monthlyPayment * newQuantity;
        setDueMonthly(dueMonthly - oldPrice + newPrice);
      }
    }
  };

  const resetSelectedFields = (fields, isCustom = false) => {
    if (fields.length > 0) {
      const updatedOrderData = { ...orderData };
      fields.forEach(field => {
        const keyPath = isCustom ? 'fields.key' : 'fields.name';
        const fieldName = _.get(field, keyPath, '');
        if (_.has(orderData, fieldName)) {
          updatedOrderData[fieldName] = '';
        }
      });
      setIsFieldReset(true);
      setOrderData(updatedOrderData);
    }
  };

  const handleFormField = (
    { name, value, checked },
    isFieldModified,
    fileUploded
  ) => {
    const fields = { ...formFields };
    let needToCheck = 1;
    const addAmountToNow = 0;
    const addAmountToMonthly = 0;
    let subtractAmountFromNow = 0;
    let subtractAmountFromMonthly = 0;

    setIsFieldReset(false);
    setIsFormValidated(false);

    if (isFieldModified && !isFormTouched) {
      setIsFormTouched(true);
    }
    fields[name] = {
      ...fields[name],
      value,
      checked,
      isVisible: true,
      touched: true
    };

    if (fields[name].type === 'MultiCheckbox') {
      fields[name].options = getMultiCheckboxOptions(
        fields[name].options,
        value,
        name,
        fields,
        orderData
      );
    }

    if (fields[name].type === 'MultiFileUpload') {
      fields[name] = { ...fields[name], fileUploded };
      formFields[name] = { ...formFields[name], fileUploded };
      formStepFields[name] = { ...formStepFields[name], fileUploded };
    }

    updateTotals(name, fields[name]);
    if (['Select', 'Radio'].includes(fields[name].type)) {
      const currentField = findField(formStepFields, name);
      if (currentField && currentField.fields && currentField.fields.options) {
        const currentOption = currentField.fields.options.find(
          item => item.fields.value === value
        );

        if (typeof currentOption !== 'undefined') {
          fields[name].price = currentOption.fields.price || 0;
          fields[name].monthlyPayment =
            currentOption.fields.monthlyPayment || 0;
        } else {
          if (fields[name].price) fields[name].price = 0;
          if (fields[name].monthlyPayment) fields[name].monthlyPayment = 0;
        }
      }
    }
    fields[keyBusinessAppPlans] = formFields[keyBusinessAppPlans] || [];

    if (name === fieldDiscountedPricing) {
      fields[keyBusinessAppPlans].forEach(data => {
        subtractAmountFromMonthly += data.monthlyCharges * data.quantity;
      });
      fields[keyBusinessAppPlans] = [];
    }

    // Form sections visibility can be related to fields and values
    updateSections(getHiddenSections(fields));

    const getDisabledFields = (data, initFields) => {
      let result = initFields;

      const filter = !data
        ? []
        : data.filter(item => {
            const { sys, fields: itemFields } = item;
            const {
              name: itemName,
              appearsAfterField,
              eligible: eligibleField,
              ineligible,
              ineligibleNote
            } = itemFields;
            const {
              contentType: {
                sys: { id }
              }
            } = sys;
            if (name === itemName) {
              if (eligibleField) {
                const {
                  fields: { value: eligibleValue }
                } = eligibleField;
                setEligible({
                  isEligible:
                    eligibleValue === value ||
                    (eligibleValue === requiredValue && !!value && value !== 0),
                  note: {}
                });
              } else if (ineligible) {
                const {
                  fields: { value: ineligibleValue }
                } = ineligible;
                const isIneligibleValue = ineligibleValue
                  .split('|')
                  .includes(value);
                setEligible({
                  isEligible: false,
                  note: isIneligibleValue ? ineligibleNote : {}
                });
              }
            }
            if (id === formFieldsGroup) {
              result = getDisabledFields(itemFields.fields, result);
            } else if (id === formSection) {
              result = getDisabledFields(itemFields.formFields, result);
            } else if (fields[itemName] && appearsAfterField) {
              const isVisible = isRelatedFieldExisted(item.fields, fields);
              if (fields[itemName].isVisible) {
                fields[itemName].isVisible = isVisible;
                return !isVisible;
              }
              fields[itemName].isVisible = isVisible;
            }
            return false;
          });

      return [...result, ...filter];
    };

    while (needToCheck > 0 && needToCheck < 5) {
      const disabledFields = getDisabledFields(formStepFields, []);

      // eslint-disable-next-line no-loop-func
      disabledFields.forEach(item => {
        const {
          fields: { name: itemName }
        } = item;

        if (
          isFieldCalculated(
            fields[itemName].type,
            fields[itemName].value,
            fields[itemName].checked,
            fields[itemName].price
          )
        ) {
          const itemPrice =
            fields[itemName].type === 'Number'
              ? Number(fields[itemName].initPrice) *
                Number(fields[itemName].value)
              : Number(fields[itemName].price);
          subtractAmountFromNow += itemPrice;
        }

        if (
          isFieldCalculated(
            fields[itemName].type,
            fields[itemName].value,
            fields[itemName].checked,
            fields[itemName].monthlyPayment
          )
        ) {
          const itemPrice =
            fields[itemName].type === 'Number'
              ? Number(fields[itemName].initMonthlyPayment) *
                Number(fields[itemName].value)
              : Number(fields[itemName].monthlyPayment);
          subtractAmountFromMonthly += itemPrice;
        }
      });

      needToCheck = disabledFields.length > 0 ? needToCheck + 1 : 0;
    }

    if (addAmountToNow || subtractAmountFromNow) {
      setDueNow(dueNow - subtractAmountFromNow + addAmountToNow);
    }
    if (addAmountToMonthly || subtractAmountFromMonthly) {
      setDueMonthly(
        dueMonthly - subtractAmountFromMonthly + addAmountToMonthly
      );
    }

    if (name === fieldSameAsAdminContact) {
      if (checked) {
        const adminName = getFieldValue('name_companyAdmin', fields) || '';
        const adminEmail = getFieldValue('email_companyAdmin', fields) || '';
        const adminContactNo =
          getFieldValue('businessPhone_companyAdmin', fields) || '';

        fields.name_ITContact.value = adminName;
        fields.email_ITContact.value = adminEmail;
        fields.telephone_ITContact.value = adminContactNo;
      } else {
        fields.name_ITContact.value = '';
        fields.email_ITContact.value = '';
        fields.telephone_ITContact.value = '';
      }
    }

    setFormFields(resetFormFields(fields, name));

    if (_.has(fields, `${name}.resetFields`)) {
      resetSelectedFields(_.get(fields, `${name}.resetFields`, []));
    } else if (_.has(fields, `${name}.customResetFields`)) {
      resetSelectedFields(_.get(fields, `${name}.customResetFields`, []), true);
    }
  };

  useEffect(() => {
    const footer = document.getElementById('footer');
    const introduction = document.getElementById('introduction');
    if (footer) footer.style.display = 'none';
    if (introduction) introduction.style.marginBottom = '7rem';

    return () => {
      const footer = document.getElementById('footer');
      const introduction = document.getElementById('introduction');
      if (footer) footer.style.display = '';
      if (introduction) introduction.style.marginBottom = '0';
    };
  }, []);

  const renderFormSummary = (formSummaryGroup, data = orderData) => {
    let summaryData = data;
    summaryData = { ...profile, ...summaryData };
    return <FormSummaryGroup data={formSummaryGroup} orderData={summaryData} />;
  };

  const onCreateNewOrder = () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      const formData = getFormData();
      createNewBusinessAppOrder(formData);
    } finally {
      setTimeout(() => setIsSubmitting(false), 2000);
    }
  };

  const getTheUpdatedLabel = (fieldName, fieldLabel) => {
    let updatedLabel = fieldLabel;
    if (
      orderData.productInOrderFor === googleWorkspace &&
      fieldName === fieldExistingCustomerBA
    ) {
      updatedLabel = googleWorkspaceCustomer;
    }
    if (
      orderData.productInOrderFor === microsoft365 &&
      fieldName === fieldExistingCustomerBA
    ) {
      updatedLabel = microsoft365Customer;
    }
    if (
      orderData.productInOrderFor === googleWorkspace &&
      fieldName === fieldExistingCustomerDomain
    ) {
      updatedLabel = googleWorkspaceDomain;
    }
    if (
      orderData.productInOrderFor === microsoft365 &&
      fieldName === fieldExistingCustomerDomain
    ) {
      updatedLabel = microsoft365Domain;
    }
    return updatedLabel;
  };

  const renderFormFields = data =>
    data &&
    data.map(({ sys, fields }, index) => {
      const {
        contentType: {
          sys: { id }
        },
        id: sysId
      } = sys;
      const { name: fieldName, title, expiredDate, type, note2 } = fields;
      const fieldValue = getFieldValue(fieldName, formFields);
      const fieldDateConfig = {};
      let selectOptions = _.get(fields, 'options', []);
      let isRequired = fields.required || false;
      const fieldNote = fields.note;
      const fieldNote2 = note2;
      let tooltipLongCopy = fields.tooltipLong; 

      if (type === 'MultiCheckbox') {
        selectOptions = formFields[fieldName].options;
      }

      const error = formErrors[fieldName];
      const key = `FormField${fieldName}${index}${sysId}`;
      let result;

      if (expiredDate && new Date(expiredDate) < new Date()) return null;
      if (!isRelatedFieldExisted(fields, formFields)) return null;


      if (fieldName === fieldAccountNumberBANBA) {
        const customerInvoiceForBA_Value = getFieldValue(
          fieldCustomerInvoiceForBA,
          formFields
        );
        if (customerInvoiceForBA_Value === newBan) {
          const existingBusinessConnectOrWirelessAccountBA_Value = getFieldValue(
            fieldExistingBusinessConnectOrWirelessAccountBA,
            formFields
          );
          if (existingBusinessConnectOrWirelessAccountBA_Value !== yes) {
            formFields.accountNumberBAN_BA.isVisible = false;
            formFields.accountNumberBAN_BA.isRequired = false;
            return null;
          }
        }
      }

      if (fieldName === fieldAttachmentsBA) {
        const valueMsaEContractBA = getFieldValue(
          fieldMsaEContractBA,
          formFields
        );
        if (
          orderData.productInOrderFor === esChat &&
          valueMsaEContractBA &&
          valueMsaEContractBA === yes
        ) {
          formFields.attachmentsBA.isRequired = true;
          isRequired = true;
        } else if (
          orderData.productInOrderFor === esChat &&
          valueMsaEContractBA &&
          valueMsaEContractBA === no
        ) {
          formFields.attachmentsBA.isRequired = false;
          isRequired = false;
        }
      }

      if (
        [
          fieldBusinessIncorporationNumberBA,
          fieldBusinessIncorporativeDateBA
        ].includes(fieldName)
      ) {
        const valueCustomerInvoiceForBA = getFieldValue(
          fieldCustomerInvoiceForBA,
          formFields
        );
        const valueExistingBusinessConnectOrWirelessAccountBA = getFieldValue(
          fieldExistingBusinessConnectOrWirelessAccountBA,
          formFields
        );
        if (
          valueCustomerInvoiceForBA &&
          valueExistingBusinessConnectOrWirelessAccountBA
        ) {
          if (
            valueCustomerInvoiceForBA === newBan &&
            valueExistingBusinessConnectOrWirelessAccountBA === yes
          ) {
            formFields.businessIncorporationNumberBA.isRequired = false;
            formFields.businessIncorporativeDateBA.isRequired = false;
            isRequired = false;
          } else {
            formFields.businessIncorporationNumberBA.isRequired = true;
            formFields.businessIncorporativeDateBA.isRequired = true;
            isRequired = true;
          }
        }
      }

      if (fieldName === fieldCustomerInvoiceForBA)
        if (fieldName === fieldAddProductBA) {
          const valueOrderRequestTypeBA = getFieldValue(
            fieldOrderRequestTypeBA,
            formFields
          );
          if (valueOrderRequestTypeBA === undefined) {
            formFields.addProductBA.isVisible = false;
            formFields.addProductBA.isRequired = false;
            return null;
          }
        }

      if (fieldName === fieldStreetNumberAddress) {
        const valueStreetNumberAddress = getFieldValue(
          fieldStreetNumberAddress,
          formFields
        );
        if (
          valueStreetNumberAddress &&
          typeof valueStreetNumberAddress === 'object'
        ) {
          formFields.streetName_address.value =
            valueStreetNumberAddress.streetName;
          formFields.city_address.value = valueStreetNumberAddress.city;
          formFields.province_address.value =
            valueStreetNumberAddress.province;
          formFields.postalCode_address.value =
            valueStreetNumberAddress.postalCode;
          formFields.streetNumber_address.value =
            valueStreetNumberAddress.streetNumber;
        }
      }

      if (fieldName === fieldMsaEContractBA) {
        if (orderData.productInOrderFor !== esChat) {
          formFields.msaEContractBA.isVisible = false;
          formFields.msaEContractBA.isRequired = false;
          return null;
        }
      }

      if (fieldName === fieldPinBA) {
        const valueCustomerInvoiceForBA =
          getFieldValue(fieldCustomerInvoiceForBA, formFields) || '';
        if (valueCustomerInvoiceForBA !== newBan) {
          formFields.pinBA.isRequired = false;
          formFields.pinBA.isVisible = false;
          return null;
        }
      }
      if (orderData.productInOrderFor === esChat) {
        if (fieldName === fieldBusinessAppBillingNoteBA) {
          return null;
        }
        if (fieldName === fieldCustomerInvoiceForBA) {
          formFields[fieldCustomerInvoiceForBA].isRequired = false;
          formFields[fieldCustomerInvoiceForBA].isVisible = false;
          return null;
        }
      }

      if (id === formFieldsGroup) {
        const groupIsVisible = fields.fields.find(item => {
          const { fields: itemFields } = item;
          if (!formFields[itemFields.name]) return null;
          const { value, isVisible } = formFields[itemFields.name];
          return (
            (value && isVisible) ||
            isRelatedFieldExisted(itemFields, formFields)
          );
        });

        if (typeof groupIsVisible !== 'undefined') {
          const groupFields = renderFormFields(fields.fields);
          if (fieldName === 'advisory') {
            result = groupFields.filter(item => item).length > 0 && (
              <GroupWrapperStyled key={key}>{groupFields}</GroupWrapperStyled>
            );
          } else {
            result = groupFields.filter(item => item).length > 0 && (
              <GroupWrapper key={key}>
                {title && <GroupTitle>{title}</GroupTitle>}
                {groupFields}
              </GroupWrapper>
            );
          }
        }
      } else if (id === formSection) {
        const { note, formSummaryGroup, isPanelInitiallyOpen } = fields;
        result = sectionIsVisible(fieldName) && (
          <ExpandCollapse
            initialOpen={isPanelInitiallyOpen ? [fieldName] : []}
            topDivider={false}
          >
            {(expandProps) => (
              <ExpandCollapse.Panel {...expandProps} key={key} panelId={fieldName} control={<SectionHeaderTitle><Typography variant={{ bold: true }}>{title}</Typography></SectionHeaderTitle>}>
            <>
              <Divider space={2} variant={{ inverse: true }} />
              {note && note.content && <Note data={note} type="richText" />}
              {formSummaryGroup
                ? renderFormSummary(formSummaryGroup)
                : renderFormFields(fields.formFields)}
            </>
              </ExpandCollapse.Panel>
            )}
          </ExpandCollapse>
        );
      } else if (id === userDetailsKey) {
        result = (
          <UserDetailsForm
            key={key}
            data={fields}
            setFormFields={setFormFields}
            orderData={orderData}
            formErrors={formErrors}
            formFields={formFields}
            isFormTouched={isFormTouched}
            setIsFormTouched={setIsFormTouched}
            resetSelectedFields={resetSelectedFields}
          />
        );
      } else if (id === repeatedFormKey) {
        result = (
          <RepeatedForm
            key={key}
            data={fields}
            setFormFields={setFormFields}
            orderData={orderData}
            formErrors={formErrors}
            formFields={formFields}
            isFormTouched={isFormTouched}
            setIsFormTouched={setIsFormTouched}
            isMultisiteOrder={false}
          />
        );
      } else {
        const fieldButtonProps =
          fields.type === 'Button'
            ? {
                modalForms,
                formFields,
                setFormFields,
                dueNow,
                setDueNow,
                dueMonthly,
                setDueMonthly,
                setIsFormTouched,
                setIsFormValidated
              }
            : {};
        let ref;
        let isDisabled;
        if (formFields[fieldName]) {
          ({ ref, isDisabled } = formFields[fieldName]);
        }
        const fieldData = {
          ...fields,
          options: selectOptions || fields.options,
          ...fieldButtonProps,
          ref,
          name: fieldName,
          value: fieldValue,
          disabled: isDisabled,
          required: isRequired,
          note: fieldNote,
          note2: fieldNote2,
          tooltipLong: tooltipLongCopy,
          sysId,
          onChange: (e, isFieldModified = true, fileUploded) =>
            handleFormField(e.target, isFieldModified, fileUploded),
          fieldDateConfig,
          formFields,
          setFormFields,
          setOrderData,
          resetSelectedFields
        };

        if (fields.type === 'Checkbox') {
          fieldData.checked = formFields[fieldName].checked;
        }

        fieldData.label = getTheUpdatedLabel(fieldName, fieldData.label);

        result = (
          <FormField
            key={key}
            orderData={orderData}
            data={fieldData}
            error={error && error.text}
          />
        );
      }
      return result;
    });

  const getUpdatedTitle = () => {
    if (step === 1 && orderData.productInOrderFor)
      return `${formStepName} - ${orderData.productInOrderFor}`;
    return formStepName;
  };

  const getSummary = () => {
    if (formSummary) {
      const {
        fields: { formSummaryGroup }
      } = formSummary;

      return formSummaryGroup.map((summaryGroup, index) => {
        const {
          fields: { name, formFields: summaryGroupFields }
        } = summaryGroup;
        const key = `formSummaryGroup${index}`;

        return (
          <Fragment key={key}>
            <h1>{name}</h1>
            <SummaryGroupWrapper>
              {summaryGroupFields.map((summaryGroupField, idx) => {
                const {
                  fields: { name: fieldName, label: fieldLabel }
                } = summaryGroupField;
                const summaryGroupFieldKey = `summaryGroupField${idx}`;

                return (
                  <div key={summaryGroupFieldKey}>
                    <span style={{ fontWeight: '400' }}>{fieldLabel}</span>:{' '}
                    {formFields[fieldName].value}
                  </div>
                );
              })}
            </SummaryGroupWrapper>
          </Fragment>
        );
      });
    }

    return Object.keys(formFields).map((item, index) => {
      const key = `summary${index}`;

      return (
        formFields[item].isVisible && (
          <div key={key}>
            <span style={{ fontWeight: '400' }}>{item}</span>:{' '}
            {formFields[item].value}
          </div>
        )
      );
    });
  };

  if (isLoading) return null;

  return (
    <div ref={formStepRef} style={{ width: '100%' }}>
      {!hideProgressBar && (
        <ProgressBar
          name={getUpdatedTitle()}
          steps={steps}
          currentStep={step}
          formName={formName}
        />
      )}
      <div style={{ backgroundColor: '#fff' }}>
        <form
          name={formStepName}
          onSubmit={e => e.preventDefault()}
          encType="multipart/form-data"
        >
          <Box
            style={{
              border: '1px solid #e3e3e3',
              boxShadow: '1px 1px 9px 4px #e3e3e3',
              padding: '2rem',
              marginBottom: '1rem'
            }}
          >
            {sections.length ? (
              <div className="expand">
                <ExpandCollapse initialOpen={[]} tag="h2" topDivider={false}>
                  {renderFormFields(formStepFields)}
                </ExpandCollapse>
              </div>
            ) : (
              <div style={{ width: '100%' }}>
                {formStepFields ? (
                  renderFormFields(formStepFields)
                ) : (
                  <>
                    <Typography heading="h3" variant={{ size: 'h3' }} block>Summary</Typography>
                    {getSummary()}
                  </>
                )}
              </div>
            )}
          </Box>
        </form>
      </div>
      {formName === businessAppsPrequalificationForm && (
        <div
          className="container 85%"
          style={{ display: 'flex', justifyContent: 'center' }}
        >
          <button
            type="button"
            onClick={onCreateNewOrder}
            disabled={isSubmitting}
            className={`frg-button ${
              !isSubmitting && prequalEligibility ? 'color-green' : 'state-disabled'
            }`}
            style={{ marginBottom: '1em' }}
          >
            {isSubmitting ? submittingButtonLabel : newOrderButtonLabel}
          </button>
        </div>
      )}
      {formName === businessAppsOrderForm && (
        <StatusBar
          isFormTouched={isFormTouched}
          isFormValidated={isFormValidated}
          dueMonthly={dueMonthly}
          dueNow={dueNow}
          onSaveAndExit={saveAndExit}
          onDownloadClick={onViewDocx}
          onProceedClick={nextStep}
          onValidateForm={validateForm}
          onOpenOrderDetail={handleViewOrder}
          statusQuote={_.get(orderData, 'quoteStatus', '')}
          channelName={_.get(orderData, 'channelName', '')}
          isFieldReset={isFieldReset}
          isOrderSummary={step === 2}
          onPrintPDF={onViewQuoteAsDocx}
          onSubmit={nextStep}
          onBack={prevStep}
          onCancel={onCancel}
          isEligible={
            formFields.businessAppPlans &&
            formFields.businessAppPlans.length > 0
          }
          onSaveQuoteAsDocx={onViewQuoteAsDocx}
          selectedProductIsESChat={orderData.productInOrderFor === esChat}
        />
      )}
      {orderSummary && (
        <OrderDetails
          open={isOderDetailsOpen}
          setOpen={setIsOrderDetailsOpen}
          order={orderData}
          userRights={userRights}
          comments={orderComments}
          summary={orderSummary}
          isSalesManager={isSalesManager || false}
          isSalesSupport={isSalesSupport || false}
          isAdmin={isAdmin || false}
          isSalesRep={isSalesRep || false}
        />
      )}
      {/* {
        <Modal
          heading={modalData.title}
          bodyText={modalData.body}
          confirmCTAText={modalData.confirmText}
          cancelCTAText={modalData.cancelText}
          onConfirm={() =>
            history.push({
              pathname: '/'
            })
          }
          onClose={() => setModalData({ ...modalData, open: false })}
          isOpen={modalData.open}
          focusElementAfterClose={formStepRef}
        />
      } */}
    </div>
  );
};

FormStepBusinessApp.propTypes = {
  stepData: PropTypes.shape({
    fields: PropTypes.objectOf(PropTypes.shape({})).isRequired
  }).isRequired,
  steps: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  step: PropTypes.number.isRequired,
  setEligible: PropTypes.func,
  onFormSubmit: PropTypes.func,
  onSaveAndExit: PropTypes.func,
  onCancel: PropTypes.func,
  orderData: PropTypes.objectOf(PropTypes.shape({})),
  hideProgressBar: PropTypes.bool,
  setOrderData: PropTypes.func,
  formName: PropTypes.string.isRequired,
  history: PropTypes.shape({
    push: PropTypes.func
  }).isRequired,
  prevStep: PropTypes.func.isRequired,
  onSave: PropTypes.func,
  onViewDocx: PropTypes.func,
  createNewBusinessAppOrder: PropTypes.func.isRequired
};

FormStepBusinessApp.defaultProps = {
  setEligible: null,
  onFormSubmit: null,
  orderData: {},
  onSaveAndExit: null,
  hideProgressBar: false,
  setOrderData: () => {},
  onSave: null,
  onViewDocx: null
};

export default FormStepBusinessApp;
